import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getInitialViewState } from 'components/map/location/defaultMapLocation';
import { motion } from 'framer-motion';
import { WorldMapStories_publishedStoriesForWorldMap } from 'graphql/query/__generated__/WorldMapStories';
import { WorldMapView_worldMapView } from 'graphql/query/__generated__/worldMapView';
import { useZoomToMarker } from 'hooks/map/useZoomToMarker';
import { SVG_Logo } from 'libs/images/SVG_Logo';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EColors } from 'libs/styles/styling';
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import { CustomFooter } from 'pages/custom/shared/CustomFooter';
import { CustomHeader } from 'pages/custom/shared/CustomHeader';
import { CustomMap } from 'pages/custom/shared/CustomMap';
import { CustomMapFloors } from 'pages/custom/shared/CustomMapFloors';
import { useCustomMapPage } from 'pages/custom/shared/CustomMapPage/useCustomMapPage';
import { useUrlGuard } from 'pages/custom/shared/CustomMapPage/useUrlGuard';
import { CompanyButtons } from 'pages/custom/shared/detail/CompanyButtons';
import { InteractiveMapMessage } from 'pages/custom/shared/interactive/InteractiveMapMessage';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useContext, useEffect, useState } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { IMapProps, MarkersCustomConfigProps } from 'react-give-me-map/dist/utils/map/mapTypes';
import { MapRef } from 'react-map-gl';
import { useParams } from 'react-router';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import styled from 'styled-components/macro';
import { useUpdateEffect } from 'usehooks-ts';

export interface CustomMapPageProps {
    preloadedData?: {
        worldMap: WorldMapView_worldMapView;
        stories: WorldMapStories_publishedStoriesForWorldMap[];
    };
    mapId: string;
    header?: {
        logo?: string;
        logoText?: string;
        logoSubtext?: string;
        link?: string;
        linkText?: string;
        logoMarginTop?: string;
    };
    interactiveMessage?: boolean;
    automaticHeader?: boolean;
    controls?: JSX.Element;
    markerIdActions?: string;
    onMarkerSelected?: (markerId: string) => boolean | undefined;
    onMarkerSelectedOverride?: (data: { storyId: string; markerId: string }) => void;
    mapProps?: Partial<IMapProps>;
    mapChildren?: JSX.Element;
    topOverflow?: JSX.Element;
    topOverflowPosition?: 'left' | 'right';
    highlightedStyle?: string;
    overrideMapMarkers?: IWorldMarker[];
    inlineFooterButtons?: boolean;
    interactiveStyle?: 'Style1' | 'Mini' | 'Bubble';
    disableSimpleGestures?: boolean;
    forceInitialPositionBounds?: boolean;
    mapheimLogo?: boolean;
    onMapLoaded?: (mapRef: MapRef) => void;
    newMapPosition?:
        | { type: 'marker'; markerId: string; zoomTo: boolean }
        | { type: 'reset' }
        | null;
    newVisiblMapSetIds?: string[] | null;
    forceResizeHash?: string;
    hideFloors?: boolean;
    orderedMarkerIds?: string[];
    customClickableMarkerIds?: Set<string>;
    markersCustomConfig?: MarkersCustomConfigProps;
    children?: React.ReactNode;
}

export const CustomMapPage = (props: CustomMapPageProps): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxModal = useContext(CtxModal);
    const isWide = useIsWide();

    useCustomMapPage(props.mapId, { skip: Boolean(props.preloadedData) });
    useUrlGuard(props.mapId);

    useEffect(() => {
        if (props.preloadedData) {
            ctxView.setMap(props.preloadedData.worldMap);
            ctxView.setAllDetails(props.preloadedData.stories);
        }
    }, [props.preloadedData]);

    const [showInteractiveMessage, setShowInteractiveMessage] = useState<boolean>(false);
    const [interactiveMessageWasShown, setInteractiveMessageWasShown] = useState<boolean>(false);
    const [interactiveFinished, setInteractiveFinished] = useState<boolean>(false);
    const { detailId } = useParams() as { detailId: string };
    const { zoomToMarker } = useZoomToMarker();

    const [highlightedMarkerIds, setHighlightedMarkerIds] = useState<string[]>([]);

    useEffect(() => {
        if (!ctxView.mapRef?.current) {
            return;
        }

        if (props.newMapPosition?.type === 'reset') {
            const initialViewState = getInitialViewState(ctxView.map, isWide);
            if (initialViewState.bounds) {
                ctxView.mapRef.current.fitBounds(initialViewState.bounds);
            } else {
                ctxView.mapRef.current.setCenter({
                    lat: initialViewState.latitude,
                    lng: initialViewState.longitude
                });
                ctxView.mapRef.current.setZoom(initialViewState.zoom);
            }
            setHighlightedMarkerIds([]);
        } else if (props.newMapPosition?.type === 'marker') {
            if (props.newMapPosition.zoomTo) {
                zoomToMarker(props.newMapPosition.markerId);
            }
            setHighlightedMarkerIds([props.newMapPosition.markerId]);
        } else {
            setHighlightedMarkerIds([]);
        }
    }, [props.newMapPosition, ctxView.mapRef?.current]);

    useEffect(() => {
        ctxView.mapRef?.current?.resize();
    }, [props.forceResizeHash, ctxView.mapRef?.current]);

    useEffect(() => {
        if (!ctxView.mapRef?.current || props.newVisiblMapSetIds === undefined) {
            return;
        }

        const newMapSetIds =
            props.newVisiblMapSetIds ?? ctxView.map.worldMapSets.map((e) => e.worldMapSetId) ?? [];

        ctxView.setVisibleMapSetIds(newMapSetIds);
    }, [props.newVisiblMapSetIds, ctxView.mapRef?.current]);

    useEffect(() => {
        if (interactiveMessageWasShown) {
            return;
        }
        if (ctxView.conectedDetails?.length > 0 && props.interactiveMessage) {
            setShowInteractiveMessage(true);
            setInteractiveMessageWasShown(true);
        }
    }, [props.mapId, ctxView.conectedDetails]);

    useUpdateEffect(() => {
        ctxView.resetMap();
    }, [props.mapId]);

    useEffect(() => {
        if (props.interactiveStyle) {
            ctxView.setCustomPageConfig((config) => ({
                ...config,
                markerDetailStyle: props.interactiveStyle
            }));
        }
    }, [props.interactiveStyle, props.mapId]);

    if (!ctxView.map || props.mapId !== ctxView.map.worldMapId) {
        return null;
    }

    function isSomethingFromHeader() {
        return (
            props.header?.logo ||
            props.header?.logoText ||
            props.header?.logoSubtext ||
            props.header?.link ||
            props.header?.linkText
        );
    }

    let controls = props.controls;

    if (props.automaticHeader) {
        controls = <CompanyButtons noDetails={ctxView?.conectedDetails?.length === 0} />;
    }

    return (
        <S_Page className="givememap">
            <>
                {isWide && (
                    <>
                        {(props.header || controls) && (
                            <CustomHeader {...props.header}>{controls}</CustomHeader>
                        )}
                    </>
                )}
                {!isWide && isSomethingFromHeader() && (
                    <CustomHeader {...props.header}>{controls}</CustomHeader>
                )}
                <S_MapWrapper>
                    {!props.hideFloors && <S_Floors />}
                    {props.mapheimLogo && (
                        <S_MapheimLogo {...conditionalScaleAnimation(true, 1.1, 0.9)}>
                            <S_Logo
                                onClick={() => {
                                    window.open('/', '_blank', 'noreferrer');
                                }}
                            />
                        </S_MapheimLogo>
                    )}
                    {showInteractiveMessage && !detailId && (
                        <InteractiveMapMessage
                            onChangeShown={(value) => {
                                setShowInteractiveMessage(value);
                                setInteractiveFinished(true);
                            }}
                        />
                    )}
                    {interactiveFinished && (
                        <S_InteractiveFinished
                            onClick={() => {
                                setShowInteractiveMessage(true);
                                setInteractiveFinished(false);
                                ctxModal.setDetailModal(null);
                                ctxModal.setShowFilters(false);
                                ctxModal.setDetailListModal(null);
                                ctxModal.setShowListOfStories(false);
                            }}
                        >
                            <S_Icon icon={['fal', 'bullseye-pointer']} />
                        </S_InteractiveFinished>
                    )}
                    <CustomMap
                        onMarkerSelected={props.onMarkerSelected}
                        onMarkerSelectedOverride={props.onMarkerSelectedOverride}
                        mapProps={props.mapProps}
                        topOverflow={props.topOverflow}
                        topOverflowPosition={props.topOverflowPosition}
                        highlightedStyle={props.highlightedStyle}
                        overrideMapMarkers={props.overrideMapMarkers}
                        mapChildren={props.mapChildren}
                        disableSimpleGestures={props.disableSimpleGestures}
                        forceInitialPositionBounds={props.forceInitialPositionBounds}
                        onMapLoaded={props.onMapLoaded}
                        highlightedMarkerIds={highlightedMarkerIds}
                        orderedMarkerIds={props.orderedMarkerIds}
                        customClickableMarkerIds={props.customClickableMarkerIds}
                        markersCustomConfig={props.markersCustomConfig}
                    />
                </S_MapWrapper>
                {!isWide && (
                    <>
                        {controls && (
                            <CustomFooter inlineButtons={props.inlineFooterButtons}>
                                {controls}
                            </CustomFooter>
                        )}
                    </>
                )}
            </>

            {props.children}
        </S_Page>
    );
};

const S_Page = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
    position: relative;
`;

const S_MapWrapper = styled.div`
    display: flex;
    position: relative;
    height: 100%;
`;

const S_InteractiveFinished = styled.div`
    position: absolute;
    bottom: 5px;
    left: 5px;
    background-color: white;
    z-index: 99999;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    cursor: pointer;
`;

const S_Icon = styled(FontAwesomeIcon)`
    font-size: 20px;
    color: ${EColors.ALMOST_BLACK};
`;

const S_MapheimLogo = styled(motion.div)`
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 2;
`;

const S_Logo = styled(SVG_Logo)`
    cursor: pointer;
    svg {
        filter: drop-shadow(2px 2px 1px rgb(0 0 0 / 0.7));
        width: auto;
        height: 40px;
    }
    @media only screen and (min-width: 1260px) {
        svg {
            height: 50px;
        }
    }
`;

const S_Floors = styled(CustomMapFloors)`
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 2;
`;
